import Button from '@rio-cloud/rio-uikit/Button';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import Table, { TableBody, TableColumn, TableHeader, TableHeaderColumn, TableRow } from '@rio-cloud/rio-uikit/Table';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useAppDispatch, useAppSelector } from '../../../../../configuration/setup/hooks';
import { deleteAssociation } from '../../../actions/assets/Assets.actions';
import {
    getAssets,
    getFetchingSelectedDevice,
    getSelectedAssetId,
    getSelectedAssociatedDevices,
    openEasyOnboardingDialog,
} from '../../../reducers/assets/assetsSlice';
import TelematicsName from '../TelematicsName';
import { type Asset, AssetStatus, AssetType, type AssociatedDevice, type Device } from '../types';
import { DeviceListDeletionButton } from './DeviceListDeletionButton';
import { DeviceListDeletionDialog } from './DeviceListDeletionDialog';

export const DeviceList = () => {
    const dispatch = useAppDispatch();

    const assets = useAppSelector(getAssets);
    const selectedAssetId = useAppSelector(getSelectedAssetId);
    const fetchingDevices = useAppSelector(getFetchingSelectedDevice);
    const associatedDevices = useAppSelector(getSelectedAssociatedDevices);

    const [displayDeviceDeleteDialog, setDisplayDeviceDeleteDialog] = useState<boolean>(false);
    const [devicesToDisassociate, setDevicesToDisassociate] = useState<Device[]>([]);
    const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);

    useEffect(() => {
        if (selectedAssetId !== null) {
            setSelectedAsset(assets.find(asset => asset.id === selectedAssetId) ?? null);
        } else {
            setSelectedAsset(null);
        }
    }, [assets, selectedAssetId]);

    const associateDevice = () => {
        // (selected) asset is always set when the device list tab is open
        if (selectedAssetId !== null) {
            dispatch(openEasyOnboardingDialog(selectedAssetId));
        }
    };

    const abortDelete = () => {
        closeDialogAndClearDevice();
    };

    const closeDialogAndClearDevice = () => {
        setDisplayDeviceDeleteDialog(false);
        setDevicesToDisassociate([]);
    };

    const disassociateDevices = (devices: Device[]) => () => {
        for (const device of devices) {
            const assocToDelete = {
                // biome-ignore lint/style/noNonNullAssertion: suppress out of convenience during migration to biome
                id: associatedDevices.find(associatedDevice => associatedDevice.device.id === device.id)!.associationId,
                device_id: device.id,
                // biome-ignore lint/style/noNonNullAssertion: suppress out of convenience during migration to biome
                asset_id: selectedAssetId!,
            };
            void dispatch(deleteAssociation(assocToDelete, device.type));
            closeDialogAndClearDevice();
        }
    };

    const onDisassociationButtonClick = (devices: Device[]) => {
        setDisplayDeviceDeleteDialog(true);
        setDevicesToDisassociate(devices);
    };

    const deviceList =
        associatedDevices.length > 0 ? (
            <div>
                <div data-cy='detail-view-data-sources'>
                    <Table rowKey='id' border={true} rounded={true} condensed className='shadow-subtle'>
                        <TableHeader>
                            <TableHeaderColumn columnKey='type' width='40%'>
                                <FormattedMessage id={'intl-msg:asset.devices.device.type'} />
                            </TableHeaderColumn>
                            <TableHeaderColumn columnKey='identification' width='40%'>
                                <FormattedMessage id={'intl-msg:asset.devices.device.identification'} />
                            </TableHeaderColumn>
                            <TableHeaderColumn columnKey='actions' width='20%' />
                        </TableHeader>
                        <TableBody>
                            {associatedDevices.map((associatedDevice: AssociatedDevice) => (
                                <TableRow key={associatedDevice.device.id} id={associatedDevice.device.id}>
                                    <TableColumn columnKey='type'>
                                        <div className='user-select-all'>
                                            <TelematicsName deviceType={associatedDevice.device.type} />
                                        </div>
                                    </TableColumn>
                                    <TableColumn columnKey='identification'>
                                        <div className='user-select-all' data-cy='device-identification'>
                                            {associatedDevice.device.identification}
                                        </div>
                                    </TableColumn>
                                    <TableColumn columnKey='actions' horizontalAlign='right'>
                                        <DeviceListDeletionButton
                                            associatedDevice={associatedDevice}
                                            allAssociatedDevices={associatedDevices}
                                            onDisassociationButtonClick={onDisassociationButtonClick}
                                        />
                                    </TableColumn>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
                <DeviceListDeletionDialog
                    devicesToDisassociate={devicesToDisassociate}
                    displayDeviceDeleteDialog={displayDeviceDeleteDialog}
                    disassociateDevices={disassociateDevices}
                    abortDelete={abortDelete}
                />
            </div>
        ) : (
            <div className={'callout callout-info margin-top-20'}>
                {selectedAsset?.type !== AssetType.trailer ? (
                    <span>{<FormattedMessage id={'intl-msg:create.device-connect-message.details'} />}</span>
                ) : (
                    <div />
                )}
            </div>
        );

    const archivedAssetMessage = (
        <div className={'callout callout-info margin-top-20'}>
            <FormattedMessage id={'intl-msg:create.device-connect-message.archived-asset'} />{' '}
            <FormattedMessage id={'intl-msg:create.device-connect-message.activate-asset-to-connect-device'} />
        </div>
    );

    const associateDeviceButton = (
        <div className='table-toolbar-column text-center padding-top-25 padding-bottom-15'>
            <Button
                className='btn-link-inline'
                variant={Button.VARIANT_LINK_INLINE}
                id='associateDevice'
                data-cy='associate-device'
                autoTrackingKey='associate-device'
                onClick={associateDevice}
            >
                <span className='rioglyph rioglyph-plus' />
                <FormattedMessage id={'intl-msg:create.device-connect-message.connect'} />
            </Button>
        </div>
    );

    const sidebarTabContent =
        selectedAsset?.status === AssetStatus.archived ? (
            archivedAssetMessage
        ) : (
            <div>
                {deviceList}
                {selectedAsset?.type !== AssetType.trailer ? associateDeviceButton : <div />}
            </div>
        );

    return (
        <div>
            {fetchingDevices ? (
                <Spinner text={<FormattedMessage id={'intl-msg:spinner.loading'} />} />
            ) : (
                sidebarTabContent
            )}
        </div>
    );
};
